import { Link, Navigate, useLocation } from "react-router-dom";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { PageHeroCard } from "@/components/site/PageHeroCard";
import { Button } from "@/components/ui/button";
import { openCookieSettings } from "@/components/site/CookieBanner";
import { useLang } from "@/i18n/LangContext";
import {
  getLegalDoc,
  getLegalDocByPath,
  type LegalBlock,
  type LegalDocId,
} from "@/data/legalPages";
import { legalPath } from "@/data/legalPaths";
import { PageMeta } from "@/components/site/PageMeta";

type LegalDocumentProps = {
  docId?: LegalDocId;
};

const BlockBody = ({ block }: { block: LegalBlock }) => (
  <>
    {block.paragraphs?.map((paragraph) => (
      <p key={paragraph.slice(0, 64)} className="mt-3 text-base leading-relaxed text-muted-foreground">
        {paragraph}
      </p>
    ))}
    {block.list?.length ? (
      <ul className="mt-3 list-disc space-y-2 pl-5 text-base leading-relaxed text-muted-foreground">
        {block.list.map((item) => (
          <li key={item.slice(0, 64)}>{item}</li>
        ))}
      </ul>
    ) : null}
  </>
);

const LegalDocument = ({ docId }: LegalDocumentProps) => {
  const { lang } = useLang();
  const { pathname } = useLocation();
  const doc = docId ? getLegalDoc(docId) : getLegalDocByPath(pathname);


  if (!doc) {
    return <Navigate to={lang === "sv" ? "/" : "/en"} replace />;
  }

  const copy = doc.content[lang];
  const pageTitle = `${copy.title} | Intygsläkaren`;
  const privacyHref = legalPath("privacy", lang);
  const cookiesHref = legalPath("cookies", lang);
  const termsHref = legalPath("terms", lang);
  const contactHref = lang === "sv" ? "/kontakt" : "/en/contact";

  const relatedLinks = [
    {
      show: doc.id !== "privacy",
      to: privacyHref,
      label: lang === "sv" ? "Integritetspolicy" : "Privacy policy",
    },
    {
      show: doc.id !== "cookies",
      to: cookiesHref,
      label: lang === "sv" ? "Cookie-policy" : "Cookie policy",
    },
    {
      show: doc.id !== "terms",
      to: termsHref,
      label: lang === "sv" ? "Användarvillkor" : "Terms of use",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <PageMeta title={pageTitle} description={copy.metaDescription} />
      <Header />
      <main>
        <PageHeroCard compact>
          <div className="animate-fade-up max-w-2xl">
            <h1 className="text-balance font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              {copy.title}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              {copy.intro}
            </p>
            {doc.id !== "cookies" ? (
              <div className="mt-7">
                <Button asChild variant="hero" size="lg">
                  <Link to={contactHref}>{lang === "sv" ? "Kontakta oss" : "Contact us"}</Link>
                </Button>
              </div>
            ) : null}
          </div>
        </PageHeroCard>

        <section className="py-14 lg:py-20">
          <div className="container-wide">
            <div className="space-y-12">
              {copy.sections.map((section) => {
                const isCookieManageSection =
                  doc.id === "cookies" &&
                  (section.heading === "Hantera dina cookie-inställningar" ||
                    section.heading === "Manage your cookie settings");

                return (
                  <div key={section.heading}>
                    <h2 className="font-display text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
                      {section.heading}
                    </h2>
                    <BlockBody block={section} />

                    {isCookieManageSection ? (
                      <div className="mt-6 space-y-3 rounded-2xl border border-border/60 bg-white p-5 shadow-card sm:rounded-3xl sm:p-6">
                        {[
                          {
                            title: lang === "sv" ? "Nödvändiga cookies" : "Necessary cookies",
                            text:
                              lang === "sv"
                                ? "Krävs för grundläggande webbplatsfunktioner"
                                : "Required for basic website functions",
                            alwaysOn: true,
                          },
                          {
                            title: lang === "sv" ? "Analytiska cookies" : "Analytics cookies",
                            text:
                              lang === "sv"
                                ? "Hjälper oss förbättra webbplatsen"
                                : "Help us improve the website",
                          },
                          {
                            title: lang === "sv" ? "Funktionella cookies" : "Functional cookies",
                            text:
                              lang === "sv"
                                ? "Förbättrar din användarupplevelse"
                                : "Improve your user experience",
                          },
                          {
                            title: lang === "sv" ? "Marknadsföringscookies" : "Marketing cookies",
                            text:
                              lang === "sv"
                                ? "För personaliserade annonser"
                                : "For personalised ads",
                          },
                        ].map((row) => (
                          <div
                            key={row.title}
                            className="flex items-center justify-between gap-4 rounded-xl border border-border/60 bg-white px-4 py-3"
                          >
                            <div>
                              <p className="font-semibold text-foreground">{row.title}</p>
                              <p className="text-sm text-muted-foreground">{row.text}</p>
                            </div>
                            {row.alwaysOn ? (
                              <span className="shrink-0 text-sm font-medium text-muted-foreground">
                                {lang === "sv" ? "Alltid aktiva" : "Always on"}
                              </span>
                            ) : (
                              <Button type="button" size="sm" variant="hero" onClick={openCookieSettings}>
                                {lang === "sv" ? "Hantera" : "Manage"}
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {section.subsections?.length ? (
                      <div
                        className={
                          section.subsections.length >= 2
                            ? "mt-6 grid gap-4 md:grid-cols-2"
                            : "mt-6 space-y-6"
                        }
                      >
                        {section.subsections.map((sub) => (
                          <div
                            key={sub.heading ?? sub.paragraphs?.[0]?.slice(0, 32)}
                            className="rounded-2xl border border-border/60 bg-white p-5 shadow-card sm:rounded-3xl sm:p-6"
                          >
                            {sub.heading ? (
                              <h3 className="font-display text-lg font-semibold text-foreground">
                                {sub.heading}
                              </h3>
                            ) : null}
                            <BlockBody block={sub} />
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}

              <p className="text-center text-sm text-muted-foreground">
                {lang === "sv" ? "Senast uppdaterad" : "Last updated"}: {copy.updated}
              </p>

              <nav
                className="rounded-2xl border border-border/60 bg-white p-5 text-sm text-muted-foreground shadow-card sm:rounded-3xl"
                aria-label={lang === "sv" ? "Relaterade policyer" : "Related policies"}
              >
                <p className="font-medium text-foreground">
                  {lang === "sv" ? "Relaterade sidor" : "Related pages"}
                </p>
                <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-2">
                  {relatedLinks
                    .filter((item) => item.show)
                    .map((item) => (
                      <li key={item.to}>
                        <Link to={item.to} className="text-primary hover:underline">
                          {item.label}
                        </Link>
                      </li>
                    ))}
                </ul>
              </nav>
            </div>
          </div>
        </section>

        <Breadcrumbs
          items={[
            { label: lang === "sv" ? "Hem" : "Home", to: lang === "sv" ? "/" : "/en" },
            { label: copy.title },
          ]}
        />
      </main>
      <Footer />
    </div>
  );
};

export default LegalDocument;
